import { isBefore } from 'date-fns';
import { useMemo } from 'react';
import type { ItemTypeOption } from 'src/lib/api/types/loop-visualization';
import type { ItemV2 } from '@/lib/api/types/items';
import type { TimelineItemType } from './useTimelineTableColumns';
import { getNormalizePlannedDates } from './utils/timelineDateUtils';

export const useTimelineItemsWithChildren = ({
  from,
  to,
  items,
  targetLevelItemTypes,
  nextLevelItemTypes,
  showChildItems,
  childIdsByParentId,
  childrenByParentId,
  loadingParentIds,
}: {
  from: string | null;
  to: string | null;
  items: ItemV2[];
  targetLevelItemTypes: ItemTypeOption[];
  nextLevelItemTypes: ItemTypeOption[];
  showChildItems: boolean;
  childIdsByParentId: ReadonlyMap<number, number[]>;
  childrenByParentId: ReadonlyMap<number, Partial<ItemV2>[]>;
  loadingParentIds: ReadonlySet<number>;
}): TimelineItemType[] => {
  return useMemo<TimelineItemType[]>(() => {
    if (!from || !to) {
      return [];
    }
    const targetLevelItemTypeIds = new Set(targetLevelItemTypes.map((t) => t.id));
    const targetRows = items.filter((item) => targetLevelItemTypeIds.has(item.itemTypeId));

    if (!showChildItems) {
      return targetRows;
    }

    const nextLevelItemTypeIds = new Set(nextLevelItemTypes.map((t) => t.id));

    const isVisibleChild = (item: Partial<ItemV2>): boolean => {
      const childDates = getNormalizePlannedDates(
        item.plannedStartDateTime,
        item.plannedFinishDateTime,
      );
      return Boolean(
        childDates.hasAnyDate &&
          childDates.start &&
          childDates.finish &&
          item.itemTypeId != null &&
          nextLevelItemTypeIds.has(item.itemTypeId) &&
          !isBefore(to, childDates.start) &&
          !isBefore(childDates.finish, from),
      );
    };

    // Matched next-level descendants are eagerly present in `items` (not in the
    // lazy childrenByParentId), so collect them here too.
    const targetIds = new Set(targetRows.map((row) => row.id));
    const eagerChildrenByParentId = new Map<number, ItemV2[]>();
    for (const item of items) {
      if (
        item.parentId == null ||
        targetIds.has(item.id) ||
        !nextLevelItemTypeIds.has(item.itemTypeId)
      ) {
        continue;
      }
      const siblings = eagerChildrenByParentId.get(item.parentId);
      if (siblings) siblings.push(item);
      else eagerChildrenByParentId.set(item.parentId, [item]);
    }

    return targetRows.map((item) => {
      const childById = new Map<number, Partial<ItemV2>>();
      for (const child of eagerChildrenByParentId.get(item.id) ?? [])
        childById.set(child.id, child);
      for (const child of childrenByParentId.get(item.id) ?? [])
        if (child.id != null) childById.set(child.id, child);

      const visibleChildren = [...childById.values()].filter(isVisibleChild) as TimelineItemType[];

      return {
        ...item,
        hasChildren: childIdsByParentId.has(item.id) || childById.size > 0,
        isLoadingChildren: loadingParentIds.has(item.id),
        children: visibleChildren,
      };
    });
  }, [
    from,
    to,
    items,
    targetLevelItemTypes,
    nextLevelItemTypes,
    showChildItems,
    childIdsByParentId,
    childrenByParentId,
    loadingParentIds,
  ]);
};
